import { useCallback, useEffect, useRef, useState } from 'react';

// 유휴(idle) 세션 타임아웃 훅.
// timeout 동안 사용자 활동이 없으면 onIdle() 호출(로그아웃), 만료 promptBefore 전에는 경고 노출.
// 1초 간격 폴링으로 마지막 활동 시각만 비교 → 이벤트마다 타이머를 재설정하지 않아 부하가 적음.

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'wheel'];

export default function useIdleTimeout({ timeout, promptBefore, onIdle }) {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(promptBefore);

  const lastActivity = useRef(Date.now());
  // 경고 노출 중에는 우발적 마우스 이동으로 세션이 연장되지 않도록 활동 갱신을 멈춘다.
  const warnedRef = useRef(false);
  // onIdle 이 매 렌더마다 새 함수여도 이펙트가 재실행되지 않도록 ref 로 최신값 유지.
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const reset = useCallback(() => {
    lastActivity.current = Date.now();
    warnedRef.current = false;
    setShowWarning(false);
  }, []);

  useEffect(() => {
    const onActivity = () => {
      if (warnedRef.current) return; // 경고 중에는 명시적 "연장"만 세션을 되살린다
      lastActivity.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const tick = setInterval(() => {
      const idleFor = Date.now() - lastActivity.current;
      if (idleFor >= timeout) {
        clearInterval(tick);
        onIdleRef.current();
      } else if (idleFor >= timeout - promptBefore) {
        warnedRef.current = true;
        setShowWarning(true);
        setRemainingMs(timeout - idleFor);
      }
    }, 1000);

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(tick);
    };
  }, [timeout, promptBefore]);

  return { showWarning, remainingMs, extend: reset };
}
